import { Button, Spinner } from '@crm/ui';
import { Check, RotateCcw } from 'lucide-react';
import { useState } from 'react';
import { EmptyState } from '@/components';
import {
  useCreateServiceTemplate,
  useDeleteServiceTemplate,
  useTemplates,
  useUpdateServiceTemplate,
} from '../api';
import { CategoryTemplateCard } from './category-template-card';
import type { ServicoEdicao } from './service-template-row';

/**
 * A lista pronta que o assistente de configuracoes oferece a cada salao
 * novo: categorias com foto e os servicos mais comuns de cada uma. O que
 * muda aqui vale para todo salao que ainda nao passou pelo assistente.
 *
 * As cinco categorias sao fixas (Cabelo, Unhas, Barba, Sobrancelhas, Pele) -
 * o passo de cadastro do CTA pergunta sim/nao por uma delas antes de pedir
 * foto e servico, entao criar ou renomear uma faria a pergunta e a categoria
 * dessincronizarem. Da para editar a foto e os servicos de cada uma.
 *
 * Edicao de servico existente nao salva sozinha: fica pendente ate o
 * Salvar/Resetar unico no rodape, que manda (ou descarta) tudo de uma vez -
 * nao um botao por linha. Criar e remover servico continuam imediatos.
 */
export function TemplatesAssistant() {
  const templates = useTemplates();
  const createService = useCreateServiceTemplate();
  const updateService = useUpdateServiceTemplate();
  const deleteService = useDeleteServiceTemplate();

  // So a mais recente comeca aberta - vinte categorias abertas ao mesmo
  // tempo era so rolar a pagina para achar a que se acabou de criar.
  const [aberta, setAberta] = useState<string | null>(null);
  const [pendentes, setPendentes] = useState<Record<string, ServicoEdicao>>({});
  const [salvando, setSalvando] = useState(false);

  const categorias = templates.data?.data ?? [];
  const idsPendentes = Object.keys(pendentes);
  const temPendencias = idsPendentes.length > 0;

  function alterarServico(id: string, input: ServicoEdicao) {
    setPendentes((atual) => ({ ...atual, [id]: { ...atual[id], ...input } }));
  }

  function resetar() {
    setPendentes({});
  }

  async function salvarTudo() {
    setSalvando(true);
    try {
      await Promise.all(
        Object.entries(pendentes).map(([id, input]) => updateService.mutateAsync({ id, input })),
      );
      setPendentes({});
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="space-y-4 pb-16">
      <p className="text-sm text-muted-foreground">
        Categorias e servicos ja prontos quando um salao novo abre o assistente de configuracoes. As
        categorias sao fixas; edite a foto e os servicos de cada uma.
      </p>

      {templates.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner className="h-6 w-6 text-muted-foreground" />
        </div>
      ) : categorias.length === 0 ? (
        <EmptyState title="Nenhuma categoria na lista pronta" />
      ) : (
        <div className="space-y-3">
          {categorias.map((categoria) => (
            <CategoryTemplateCard
              key={categoria.id}
              categoria={categoria}
              aberta={aberta === categoria.id}
              onToggle={() => setAberta((atual) => (atual === categoria.id ? null : categoria.id))}
              onCreateService={(input) =>
                createService.mutate({ categoryTemplateId: categoria.id, ...input })
              }
              pendentes={pendentes}
              onChangeService={alterarServico}
              onRemoveService={(id) => deleteService.mutate(id)}
            />
          ))}
        </div>
      )}

      {temPendencias ? (
        <div className="sticky bottom-0 -mx-4 flex items-center justify-end gap-2 border-t border-border bg-background/95 px-4 py-3 backdrop-blur">
          <span className="mr-auto text-sm text-muted-foreground">
            {idsPendentes.length} {idsPendentes.length === 1 ? 'servico' : 'servicos'} com alteracao
            nao salva
          </span>
          <Button variant="outline" onClick={resetar} disabled={salvando}>
            <RotateCcw className="h-4 w-4" />
            Resetar
          </Button>
          <Button onClick={salvarTudo} disabled={salvando}>
            {salvando ? <Spinner className="h-4 w-4" /> : <Check className="h-4 w-4" />}
            Salvar
          </Button>
        </div>
      ) : null}
    </div>
  );
}
